// Pipeline Command Centre → PDF exporter.
//
// Renders the entire on-screen analytics into a print-friendly PDF using
// jsPDF + jspdf-autotable. Everything is derived from the same `data`
// object the Pipeline UI receives — no server round-trip.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PIPELINE_STAGES, TERMINAL_STAGES } from './pipelineConstants';

const fmtMoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;
const fmtNum   = (n) => Number(n || 0).toLocaleString('en-AU');
const fmtPct   = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const fmtDays  = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}d`);

// Derwent brand tones — used consistently for section headers.
const BRAND = {
  navy:   [15, 23, 42],       // slate-900
  accent: [37, 99, 235],      // blue-600
  soft:   [241, 245, 249],    // slate-100
  muted:  [100, 116, 139],    // slate-500
  emerald:[16, 185, 129],
  rose:   [239, 68, 68],
  amber:  [245, 158, 11],
};

const PAGE_MARGIN = 14;
const PAGE_W = 210; // A4 mm
const CONTENT_W = PAGE_W - PAGE_MARGIN * 2;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const drawSectionTitle = (doc, title) => {
  const y = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : doc._customY || 40;
  // Guard against page overflow
  if (y > 265) doc.addPage();
  const cursorY = doc.lastAutoTable?.finalY && doc.lastAutoTable.finalY > 265 ? PAGE_MARGIN + 4 : y;
  doc.setFillColor(...BRAND.navy);
  doc.rect(PAGE_MARGIN, cursorY, CONTENT_W, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text(title.toUpperCase(), PAGE_MARGIN + 3, cursorY + 5);
  doc.setTextColor(0, 0, 0);
  doc._customY = cursorY + 10;
};

const tableStyle = {
  theme: 'grid',
  headStyles: {
    fillColor: BRAND.navy,
    textColor: [255, 255, 255],
    fontSize: 8,
    fontStyle: 'bold',
  },
  bodyStyles: { fontSize: 8, textColor: BRAND.navy },
  alternateRowStyles: { fillColor: BRAND.soft },
  margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  styles: { cellPadding: 1.6, overflow: 'linebreak' },
};

const runTable = (doc, opts) => {
  autoTable(doc, {
    ...tableStyle,
    startY: doc._customY || (doc.lastAutoTable?.finalY ?? 40) + 2,
    ...opts,
  });
  doc._customY = null;
};

// ─────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────
export const exportPipelinePdf = ({ data, period, customStart, customEnd, filters }) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const current = data?.current;
  if (!current) return;

  // ─── Cover header ─────────────────────────────────────────
  const generatedAt = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'short' });
  const rangeLabel = period === 'custom' && customStart && customEnd
    ? `${customStart} → ${customEnd}`
    : period.toUpperCase();

  // Brand strip
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('DERWENT', PAGE_MARGIN, 12);
  doc.setTextColor(96, 165, 250);
  doc.text('INTELLIGENCE', PAGE_MARGIN + 30, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(203, 213, 225);
  doc.text('Pipeline Command Centre Report', PAGE_MARGIN, 18);
  doc.setFontSize(8);
  doc.text(`Generated: ${generatedAt}`, PAGE_W - PAGE_MARGIN, 12, { align: 'right' });
  doc.text(`Period: ${rangeLabel}`, PAGE_W - PAGE_MARGIN, 17, { align: 'right' });

  doc.setTextColor(0, 0, 0);
  doc._customY = 28;

  // Active filters banner
  const activeFilters = Object.entries(filters || {}).filter(([, v]) => v);
  if (activeFilters.length) {
    doc.setFillColor(...BRAND.soft);
    doc.rect(PAGE_MARGIN, doc._customY, CONTENT_W, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text('FILTERS:', PAGE_MARGIN + 2, doc._customY + 4);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.navy);
    const filterText = activeFilters.map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join('   ·   ');
    doc.text(filterText, PAGE_MARGIN + 18, doc._customY + 4);
    doc._customY += 9;
  }

  // ─── 1. Executive Summary — KPIs ──────────────────────────
  drawSectionTitle(doc, 'Executive summary');
  const v = current.volumes;
  const prev = data?.previous?.volumes || {};
  const dPct = (curr, p) => {
    const c = Number(curr) || 0; const pv = Number(p) || 0;
    if (pv === 0) return c === 0 ? '—' : '+100%';
    const pct = ((c - pv) / Math.abs(pv)) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
  };
  runTable(doc, {
    head: [['Metric', 'Current period', 'vs previous']],
    body: [
      ['Total deals in period', fmtNum(v.total_deals), ''],
      ['Submitted $',           fmtMoney(v.submitted_amount), dPct(v.submitted_amount, prev.submitted_amount)],
      ['Approved $',            fmtMoney(v.approved_amount),  dPct(v.approved_amount,  prev.approved_amount)],
      ['Settled $',             fmtMoney(v.settled_amount),   dPct(v.settled_amount,   prev.settled_amount)],
      ['Approval rate',         fmtPct(v.approval_rate),      dPct(v.approval_rate,    prev.approval_rate)],
      ['Decline rate',          fmtPct(v.decline_rate),       dPct(v.decline_rate,     prev.decline_rate)],
      ['Average loan size',     fmtMoney(v.avg_loan_size),    dPct(v.avg_loan_size,    prev.avg_loan_size)],
    ],
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { halign: 'right' },
      2: { halign: 'right', textColor: BRAND.muted },
    },
  });

  // ─── 2. Client Journey Funnel ─────────────────────────────
  drawSectionTitle(doc, 'Client journey · 8-stage funnel');
  runTable(doc, {
    head: [['Stage', 'Count', 'Progress from prior stage']],
    body: PIPELINE_STAGES.map((s, i) => {
      const cnt = current.counts[s.key] || 0;
      const conv = current.conversions[i - 1];
      return [s.label, fmtNum(cnt), conv ? fmtPct(conv.rate) : '—'];
    }),
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'right' },
      2: { halign: 'right' },
    },
  });

  // Terminal stages inline
  runTable(doc, {
    head: [['Terminal state', 'Count']],
    body: TERMINAL_STAGES.map(s => [s.label, fmtNum(current.counts[s.key] || 0)]),
    columnStyles: { 1: { halign: 'right' } },
  });

  // ─── 3. Bottlenecks ───────────────────────────────────────
  drawSectionTitle(doc, 'Bottlenecks · time at each stage');
  runTable(doc, {
    head: [['Stage', 'Median days', 'p90 days', 'Currently here', 'Sample size']],
    body: current.bottlenecks
      .filter(b => PIPELINE_STAGES.find(s => s.key === b.stage))
      .map(b => {
        const stageMeta = PIPELINE_STAGES.find(s => s.key === b.stage);
        return [
          stageMeta?.label || b.stage,
          fmtDays(b.median_days),
          fmtDays(b.p90_days),
          fmtNum(b.currently_at_stage),
          fmtNum(b.sample_size),
        ];
      }),
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
    },
  });

  // ─── 4. Lender leaderboard ────────────────────────────────
  if ((current.lenders || []).length) {
    drawSectionTitle(doc, 'Lender leaderboard · turnaround detail');
    runTable(doc, {
      head: [['Bank', 'Files', 'Open', 'Appr', 'Dec', 'Set', 'TAT med', 'p90', '→Cond', 'Cond→U', 'Sub $', 'Set $']],
      body: current.lenders.map(l => [
        l.lender, l.count, l.open, l.approvals, l.declines, l.settled,
        fmtDays(l.median_turnaround_days), fmtDays(l.p90_turnaround_days),
        fmtDays(l.median_sub_to_cond), fmtDays(l.median_cond_to_uncond),
        fmtMoney(l.submitted_amount), fmtMoney(l.settled_amount),
      ]),
      styles: { ...tableStyle.styles, fontSize: 7 },
      bodyStyles: { ...tableStyle.bodyStyles, fontSize: 7 },
      headStyles: { ...tableStyle.headStyles, fontSize: 7 },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
        7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' },
        10:{ halign: 'right' }, 11:{ halign: 'right' },
      },
    });
  }

  // ─── 5. Credit team leaderboard ───────────────────────────
  if ((current.credit_team || []).length) {
    drawSectionTitle(doc, 'Credit team · assessor detail');
    runTable(doc, {
      head: [['Officer', 'Files', 'Open', 'Appr', 'Set', 'Dec', 'Hold', 'TAT med', 'p90', 'Appr %', 'Sub $']],
      body: current.credit_team.map(m => [
        m.member, m.count, m.open, m.approvals, m.settled, m.declines, m.on_hold,
        fmtDays(m.median_turnaround_days), fmtDays(m.p90_turnaround_days),
        fmtPct(m.approval_rate), fmtMoney(m.submitted_amount),
      ]),
      styles: { ...tableStyle.styles, fontSize: 7 },
      bodyStyles: { ...tableStyle.bodyStyles, fontSize: 7 },
      headStyles: { ...tableStyle.headStyles, fontSize: 7 },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
        7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' }, 10:{ halign: 'right' },
      },
    });
  }

  // ─── 6. Broker leaderboard ────────────────────────────────
  if ((current.brokers || []).length) {
    drawSectionTitle(doc, 'Broker leaderboard');
    runTable(doc, {
      head: [['Broker', 'Files', 'Submitted $', 'Approved $', 'Settled $']],
      body: current.brokers.slice(0, 20).map(b => [
        b.broker, b.count, fmtMoney(b.submitted_amount), fmtMoney(b.approved_amount), fmtMoney(b.settled_amount),
      ]),
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
      },
    });
  }

  // ─── 7. Stage Ageing — files currently stuck ──────────────
  const aged = current.stage_ageing || {};
  const flatAged = [];
  PIPELINE_STAGES.forEach(s => {
    (aged[s.key] || []).forEach(f => flatAged.push({ ...f, stage_label: s.label }));
  });
  flatAged.sort((a, b) => b.days_at_stage - a.days_at_stage);
  if (flatAged.length) {
    drawSectionTitle(doc, 'Stage ageing · files currently stuck');
    runTable(doc, {
      head: [['Client', 'Stage', 'Broker', 'Lender', 'Credit officer', 'Loan $', 'Days here']],
      body: flatAged.slice(0, 25).map(f => [
        f.client_name, f.stage_label, f.broker, f.lender, f.credit_team_member,
        fmtMoney(f.est_loan_amount), `${f.days_at_stage}d`,
      ]),
      styles: { ...tableStyle.styles, fontSize: 7 },
      bodyStyles: { ...tableStyle.bodyStyles, fontSize: 7 },
      headStyles: { ...tableStyle.headStyles, fontSize: 7 },
      columnStyles: {
        0: { fontStyle: 'bold' },
        5: { halign: 'right' },
        6: { halign: 'right', fontStyle: 'bold' },
      },
    });
  }

  // ─── 8. Open pipeline age brackets — all files listed ─────
  const bucketFiles = current.age_bracket_files || {};
  const bucketOrder = ['0-3d', '4-7d', '8-14d', '15-30d', '30d+'];
  const totalOpen = bucketOrder.reduce((s, k) => s + ((current.age_brackets || {})[k] || 0), 0);
  if (totalOpen > 0) {
    drawSectionTitle(doc, `Open pipeline · age brackets (${totalOpen} files)`);
    // Summary row per bucket
    runTable(doc, {
      head: [['Bucket', 'Count', '% of open']],
      body: bucketOrder.map(k => {
        const cnt = (current.age_brackets || {})[k] || 0;
        const pct = totalOpen ? ((cnt / totalOpen) * 100).toFixed(0) : 0;
        return [k, fmtNum(cnt), `${pct}%`];
      }),
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' } },
    });

    // Per-bucket file listings
    bucketOrder.forEach(k => {
      const files = bucketFiles[k] || [];
      if (files.length === 0) return;
      drawSectionTitle(doc, `Age bracket: ${k} · ${files.length} file${files.length === 1 ? '' : 's'}`);
      runTable(doc, {
        head: [['Client', 'Stage', 'Broker', 'Lender', 'Credit', 'Purpose', 'Loan $', 'Days open']],
        body: files.map(f => [
          f.client_name, f.stage_label, f.broker, f.lender, f.credit_team_member,
          f.loan_purpose, fmtMoney(f.est_loan_amount), `${f.days_open}d`,
        ]),
        styles: { ...tableStyle.styles, fontSize: 7 },
        bodyStyles: { ...tableStyle.bodyStyles, fontSize: 7 },
        headStyles: { ...tableStyle.headStyles, fontSize: 7 },
        columnStyles: {
          0: { fontStyle: 'bold' },
          6: { halign: 'right' },
          7: { halign: 'right', fontStyle: 'bold' },
        },
      });
    });
  }

  // ─── 9. Purpose × outcome ─────────────────────────────────
  if ((current.purpose_outcome || []).length) {
    drawSectionTitle(doc, 'Loan purpose · outcome');
    runTable(doc, {
      head: [['Purpose', 'Total', 'Open', 'Approved', 'Settled', 'Declined', 'Appr %']],
      body: current.purpose_outcome.map(p => [
        p.purpose, p.total, p.open, p.approved, p.settled, p.declined, fmtPct(p.approval_rate),
      ]),
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
      },
    });
  }

  // ─── 10. Broker × Lender matrix ───────────────────────────
  const m = current.broker_lender_matrix || { lenders: [], rows: [] };
  if (m.rows.length) {
    drawSectionTitle(doc, 'Broker × Lender matrix');
    runTable(doc, {
      head: [['Broker', ...m.lenders, 'Total']],
      body: m.rows.map(r => [r.broker, ...m.lenders.map(l => r.counts[l] || 0), r.total]),
      styles: { ...tableStyle.styles, fontSize: 7 },
      bodyStyles: { ...tableStyle.bodyStyles, fontSize: 7 },
      headStyles: { ...tableStyle.headStyles, fontSize: 7 },
      columnStyles: { 0: { fontStyle: 'bold' } },
    });
  }

  // ─── 11. Settlement forecast ──────────────────────────────
  if ((current.settlement_forecast || []).length) {
    drawSectionTitle(doc, 'Settlement forecast · next 90 days');
    runTable(doc, {
      head: [['Month', 'Deals', 'Amount']],
      body: current.settlement_forecast.map(r => [
        new Date(`${r.month}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
        r.count,
        fmtMoney(r.amount),
      ]),
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' } },
    });
  }

  // ─── 12. 12-week trend ────────────────────────────────────
  if ((current.weekly_trend || []).length) {
    drawSectionTitle(doc, '12-week trend');
    runTable(doc, {
      head: [['Week', 'Submitted', 'Approved', 'Settled', 'Declined']],
      body: current.weekly_trend.map(r => [r.week, r.sub, r.appr, r.set, r.dec]),
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
      },
    });
  }

  // ─── 13. Distributions ────────────────────────────────────
  const dists = [
    ['Lead source', 'lead_source'],
    ['Loan purpose', 'loan_purpose'],
    ['Application type', 'application_type'],
    ['Loan type', 'loan_type'],
  ];
  const distRows = dists.flatMap(([label, key]) => {
    const rows = Object.entries(current.breakdown?.[key] || {}).sort((a, b) => b[1] - a[1]);
    return rows.map(([n, c]) => [label, n, c]);
  });
  if (distRows.length) {
    drawSectionTitle(doc, 'Distribution breakdowns');
    runTable(doc, {
      head: [['Category', 'Value', 'Count']],
      body: distRows,
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        2: { halign: 'right', cellWidth: 25 },
      },
    });
  }

  // ─── 14. Reasons ──────────────────────────────────────────
  const reasonSections = [
    ['Top decline reasons',    'decline'],
    ['Top withdrawal reasons', 'withdrawal'],
    ['Top hold reasons',       'hold'],
  ];
  const reasonRows = reasonSections.flatMap(([label, key]) =>
    (current.reasons?.[key] || []).map(r => [label, r.reason, r.count])
  );
  if (reasonRows.length) {
    drawSectionTitle(doc, 'Top decline / withdrawal / hold reasons');
    runTable(doc, {
      head: [['Category', 'Reason', 'Count']],
      body: reasonRows,
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        2: { halign: 'right', cellWidth: 25 },
      },
    });
  }

  // ─── Footer with page numbers ─────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...BRAND.muted);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W - PAGE_MARGIN, 292, { align: 'right' });
    doc.text('Derwent Intelligence · Pipeline Command Centre', PAGE_MARGIN, 292);
  }

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`derwent-pipeline-${period}-${stamp}.pdf`);
};
